import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CollectionsSidebarService } from '@app/collections/components/collections-sidebar/collections-sidebar.service';
import { NotFoundError } from '@app/lingo2-errors';
import { ChangableComponent } from '@app/models/changable.component';
import { setCurrentItem } from '@app/store/actions/content.actions';
import { getLibraryRouteData } from '@app/store/reducers/library.reducer';
import { CollectionsService, ContextService, MetaService } from '@core/services/';
import { Store } from '@ngrx/store';
import {
  Collection,
  CollectionDetailsType,
  CollectionItemDetailsType,
  EContentPaneFormat,
  IContentPane,
  User,
} from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { distinctUntilKeyChanged, mergeAll, Observable, of, takeUntil, tap } from 'rxjs';
import { RESPONSE } from '../../../../express.tokens';

const details: CollectionDetailsType[] = [
  'author_id',
  'category',
  'changed_at',
  'cover_id',
  'description',
  'discount',
  'excerpt',
  'id',
  'language',
  'moderation_message',
  'moderation_status',
  'multi_cover_id',
  'price_tiers',
  'slug',
  'stats',
  'subject',
  'title',
  'type',
  'visit_info',
];

const itemDetails: CollectionItemDetailsType[] = ['id', 'content_id', 'is_open', 'sort_order', 'content'];

@Component({
  selector: 'app-collection-page',
  templateUrl: './collection-page.component.html',
  styleUrls: ['./collection-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CollectionPageComponent extends ChangableComponent implements OnInit, OnDestroy {
  private sidebarService = inject(CollectionsSidebarService);
  private collectionsService = inject(CollectionsService);
  private contextService = inject(ContextService);
  private metaService = inject(MetaService);
  protected readonly store = inject(Store);
  private response = inject(RESPONSE, { optional: true });
  deviceService = inject(DeviceDetectorService);

  public paneMain: IContentPane;
  public shareWidgetPane: IContentPane;
  public item: Collection;
  public me: User;
  public loading = true;
  public justCreated: boolean;
  private slug: string;

  public ngOnInit(): void {
    of(
      this.watchMe$, // при логине/перелогине - перегрузить контент
      this.watchRouteChanged$, // При смене роута - загрузить контент по slug
    )
      .pipe(mergeAll(), takeUntil(this.destroyed$))
      .subscribe({
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    // ??? this.librarySidebarService.disableContentPage();
    this.sidebarService.setCollection(null);
  }

  /**
   * При логине/перелогине - права пользователя могут поменяться
   */
  private get watchMe$(): Observable<User> {
    // при логине/перелогине - права пользователя могут поменяться
    return this.contextService.me$.pipe(distinctUntilKeyChanged('id')).pipe(
      tap((me: User) => {
        this.me = me;
      }),
    );
  }

  /**
   * При смене роута - загрузить контент по slug
   */
  private get watchRouteChanged$(): Observable<any> {
    return this.store.select(getLibraryRouteData).pipe(
      tap((data) => {
        this.loadCollection(data?.item);
      }),
    );
  }

  public get paneList(): IContentPane[] {
    return []; // ? this.isTeacher ? this.paneListForTeachers : this.paneListForStudent;
  }

  // public get isTeacher(): boolean {
  //   return AccountService.isAsIfTeacher(this.me);
  // }

  public trackPane(index: number, item: IContentPane): number {
    return item.order;
  }

  private initPanes() {
    this.paneMain = {
      order: 0,
      format: EContentPaneFormat['collection-full-main'],
      loaded: true,
      collections: [this.item],
      add_link: 'null',
    };

    this.shareWidgetPane = this.justCreated
      ? {
          order: 0,
          format: EContentPaneFormat['share-link-widget'],
          loaded: true,
          title: 'library.share-link-widget.title2',
          subtitle: 'library.share-link-widget.text2',
        }
      : {
          order: 0,
          format: EContentPaneFormat['share-link-widget'],
          loaded: true,
          title: 'library.share-link-widget.title1',
          subtitle: 'library.share-link-widget.text1',
        };
    this.detectChanges();
  }

  protected loadCollection(slug: string) {
    if (!slug) {
      return;
    }
    if (this.loading && slug === this.slug) {
      return;
    }

    this.slug = slug;
    this.loading = true;
    this.detectChanges();
    this.collectionsService
      .getBySlug(this.slug, details)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (item: Collection) => {
          this.loadCollectionItems(item);
          this.store.dispatch(setCurrentItem({ item }));
        },
        error: (err: any) => {
          this.handleError(err);
          this.loading = false;
        },
      });
  }

  protected loadCollectionItems(item: Collection) {
    this.collectionsService
      .getItemsById(item.id, itemDetails)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (items) => {
          this.item = new Collection(item);
          this.item.items = items.results;
          this.initPanes();
          this.sidebarService.setCollection(this.item);
          this.loading = false;
          this.detectChanges();
        },
        error: (error) => {
          this.loading = false;
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public handleError(err) {
    if (err instanceof NotFoundError) {
      this.metaService.setTitle(['Collection ?? not found']);
      // TODO remove metaService
      if (this.response) {
        this.response.statusCode = 404;
        this.response.statusMessage = 'Page Not Found';
      }
    }
  }
}
